"use client";
import { EditorProvider } from "@tiptap/react";
import { cn } from "@/lib/utils";
import { Color } from "@tiptap/extension-color";
import ListItem from "@tiptap/extension-list-item";
import TextStyle from "@tiptap/extension-text-style";
import BaseHeading from "@tiptap/extension-heading";
import { mergeAttributes } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import { Fragment } from "react";

// Create a wrapper component that handles the type casting internally
function TiptapEditorProvider(
  props: Omit<Parameters<typeof EditorProvider>[0], "extensions"> & {
    extensions: unknown;
  },
) {
  const { extensions, ...rest } = props;
  return <EditorProvider extensions={extensions as any} {...rest} />;
}

export function SafeRichtextRender({
  value,
  className,
  lastUpdated,
}: {
  value: string | null | undefined;
  className?: string;
  lastUpdated?: Date;
}) {
  try {
    if (!value) throw new Error("No content");

    const content = JSON.parse(value);
    return (
      <TiptapEditorProvider
        extensions={rtExtensions}
        editorProps={{
          attributes: {
            class: cn(className),
          },
        }}
        key={lastUpdated?.toISOString()}
        content={content}
        injectCSS={false}
        editable={false}
        enableCoreExtensions
        immediatelyRender={false}
      >
        <Fragment />
      </TiptapEditorProvider>
    );
  } catch (e) {
    return (
      <div className={cn(className)}>
        Aktuell kann der Inhalt nicht angezeigt werden. Bitte versuchen Sie es
        später erneut.
      </div>
    );
  }
}

export function RichtextRender({
  value,
  className,
  lastUpdated,
}: {
  value: any;
  className?: string;
  lastUpdated?: Date;
}) {
  return (
    <TiptapEditorProvider
      extensions={rtExtensions}
      editorProps={{
        attributes: {
          class: cn(className),
        },
      }}
      key={lastUpdated?.toISOString()}
      content={value}
      injectCSS={false}
      editable={false}
      enableCoreExtensions
    >
      <div className="rte">
        <Fragment />
      </div>
    </TiptapEditorProvider>
  );
}

export const rtExtensions = [
  Color.configure({ types: [TextStyle.name, ListItem.name] }),
  TextStyle.configure({ HTMLAttributes: {} }),
  StarterKit.configure({
    bulletList: {
      keepMarks: true,
      keepAttributes: false,
      HTMLAttributes: {
        class: "list-disc",
      },
    },
    orderedList: {
      keepMarks: true,
      keepAttributes: false,
      HTMLAttributes: {
        class: "list-decimal",
      },
    },
    horizontalRule: {
      HTMLAttributes: {
        class: "bg-gray-300 my-0 h-px w-full",
      },
    },
    blockquote: {
      HTMLAttributes: {
        class: "ml-1 border-l-2 border-gray-300 opacity-70 pl-2",
      },
    },
    heading: false,
  }),
  BaseHeading.extend({
    levels: [1, 2, 3],
    renderHTML({ node, HTMLAttributes }) {
      const level = this.options.levels.includes(node.attrs.level)
        ? node.attrs.level
        : this.options.levels[0];
      const classes: { [index: number]: string } = {
        1: "text-2xl font-bold",
        2: "text-xl font-bold",
        3: "text-lg font-semibold",
      };
      return [
        `h${level}`,
        mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
          class: `${classes[level]}`,
        }),
        0,
      ];
    },
  }).configure({ levels: [1, 2, 3] }),
];
